(function (global) {
  "use strict";

  /*
   * LT Liquid Glass Refraction v1.0.0
   * Optional, image-aware WebGL edge refraction.
   *
   * It draws only when its LT Liquid Glass motion controller renders a frame.
   * The approved CSS material remains visible underneath and is the complete
   * fallback when WebGL, CORS, or texture loading is unavailable.
   */

  const VERSION = "1.0.0";
  if (global.LTLiquidGlassRefraction?.version === VERSION) return;

  const reducedTransparencyQuery =
    typeof global.matchMedia === "function"
      ? global.matchMedia("(prefers-reduced-transparency: reduce)")
      : { matches: false };
  const reducedMotionQuery =
    typeof global.matchMedia === "function"
      ? global.matchMedia("(prefers-reduced-motion: reduce)")
      : { matches: false };
  const forcedColorsQuery =
    typeof global.matchMedia === "function"
      ? global.matchMedia("(forced-colors: active)")
      : { matches: false };
  const backdropFilterSupported = Boolean(
    global.CSS &&
      typeof global.CSS.supports === "function" &&
      (global.CSS.supports("backdrop-filter", "brightness(1)") ||
        global.CSS.supports("-webkit-backdrop-filter", "brightness(1)"))
  );

  function useAccessibleFallback() {
    return (
      !backdropFilterSupported ||
      reducedTransparencyQuery.matches ||
      reducedMotionQuery.matches ||
      forcedColorsQuery.matches
    );
  }

  const vertexSource = `
    attribute vec2 a_position;
    varying vec2 v_uv;

    void main() {
      v_uv = vec2(
        a_position.x * 0.5 + 0.5,
        0.5 - a_position.y * 0.5
      );
      gl_Position = vec4(a_position, 0.0, 1.0);
    }
  `;

  const fragmentSource = `
    #ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    #else
    precision mediump float;
    #endif

    uniform sampler2D u_texture;
    uniform vec2 u_lens_size;
    uniform vec2 u_center;
    uniform vec2 u_stage_size;
    uniform vec2 u_image_offset;
    uniform vec2 u_image_size;
    uniform mat2 u_transform;
    uniform mat2 u_normal_transform;
    uniform vec2 u_lag;
    uniform vec4 u_radii;
    uniform float u_edge_width;
    uniform float u_refraction;
    uniform float u_dispersion;
    uniform float u_optical_strength;
    uniform vec4 u_overlay;
    uniform vec3 u_adjust;

    varying vec2 v_uv;

    float roundedBoxSdf(vec2 point, vec2 halfSize, vec4 radii) {
      radii.xy = point.x > 0.0 ? radii.xy : radii.zw;
      float radius = point.y > 0.0 ? radii.x : radii.y;
      vec2 distance = abs(point) - (halfSize - vec2(radius));
      return length(max(distance, 0.0)) +
        min(max(distance.x, distance.y), 0.0) - radius;
    }

    vec3 applyAdjustments(vec3 color) {
      color *= u_adjust.x;
      float luminance = dot(color, vec3(0.2126, 0.7152, 0.0722));
      color = mix(vec3(luminance), color, u_adjust.y);
      color = (color - 0.5) * u_adjust.z + 0.5;
      return clamp(color, 0.0, 1.0);
    }

    vec2 stageToTexture(vec2 stagePoint) {
      return (stagePoint - u_image_offset) / u_image_size;
    }

    void main() {
      vec2 localPoint = (v_uv - vec2(0.5)) * u_lens_size;
      vec2 halfSize = u_lens_size * 0.5;
      float sdf = roundedBoxSdf(localPoint, halfSize, u_radii);

      if (sdf > 0.75) discard;

      float inside = 1.0 - smoothstep(-0.25, 0.75, sdf);
      float edge = smoothstep(-u_edge_width, -0.15, sdf) * inside;
      edge *= edge;

      if (edge < 0.001) {
        gl_FragColor = vec4(0.0);
        return;
      }

      float normalStep = 0.7;
      vec2 normal = normalize(vec2(
        roundedBoxSdf(localPoint + vec2(normalStep, 0.0), halfSize, u_radii) -
          roundedBoxSdf(localPoint - vec2(normalStep, 0.0), halfSize, u_radii),
        roundedBoxSdf(localPoint + vec2(0.0, normalStep), halfSize, u_radii) -
          roundedBoxSdf(localPoint - vec2(0.0, normalStep), halfSize, u_radii)
      ));

      vec2 transformedPoint = u_transform * localPoint + u_lag;
      vec2 stagePoint = u_center + transformedPoint;
      vec2 stageNormal = normalize(u_normal_transform * normal);
      float bend = u_refraction * edge * (0.82 + u_optical_strength * 0.18);
      vec2 bentStagePoint = stagePoint - stageNormal * bend;
      vec2 textureUv = stageToTexture(bentStagePoint);

      if (
        textureUv.x < 0.0 || textureUv.x > 1.0 ||
        textureUv.y < 0.0 || textureUv.y > 1.0
      ) {
        gl_FragColor = vec4(0.0);
        return;
      }

      vec2 dispersionUv =
        (stageNormal * u_dispersion * edge) / max(u_image_size, vec2(1.0));
      vec2 redUv = textureUv + dispersionUv;
      vec2 blueUv = textureUv - dispersionUv;

      if (
        redUv.x < 0.0 || redUv.x > 1.0 ||
        redUv.y < 0.0 || redUv.y > 1.0 ||
        blueUv.x < 0.0 || blueUv.x > 1.0 ||
        blueUv.y < 0.0 || blueUv.y > 1.0
      ) {
        gl_FragColor = vec4(0.0);
        return;
      }

      vec4 centerSample = texture2D(u_texture, textureUv);
      vec4 redSample = texture2D(u_texture, redUv);
      vec4 blueSample = texture2D(u_texture, blueUv);
      vec3 color = vec3(redSample.r, centerSample.g, blueSample.b);

      color = mix(color, u_overlay.rgb, u_overlay.a);
      color = applyAdjustments(color);

      float sampleAlpha = min(centerSample.a, min(redSample.a, blueSample.a));
      float alpha = edge * inside * sampleAlpha * 0.96;
      gl_FragColor = vec4(color, alpha);
    }
  `;

  function clamp(value, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, value));
  }

  function clearGlErrors(gl) {
    if (!gl) return;
    let attempts = 0;
    while (gl.getError() !== gl.NO_ERROR && attempts < 16) attempts += 1;
  }

  function compileShader(gl, type, source) {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function buildProgram(gl) {
    const vertex = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
    const fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
    if (!vertex || !fragment) {
      if (vertex) gl.deleteShader(vertex);
      if (fragment) gl.deleteShader(fragment);
      return null;
    }

    const program = gl.createProgram();
    if (!program) return null;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      return null;
    }

    return program;
  }

  function parseColor(value) {
    const match = String(value || "").match(/rgba?\(([^)]+)\)/i);
    if (!match) return [0, 0, 0, 0];
    const pieces = match[1]
      .replace(/\//g, " ")
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);

    return [
      clamp((pieces[0] || 0) / 255, 0, 1),
      clamp((pieces[1] || 0) / 255, 0, 1),
      clamp((pieces[2] || 0) / 255, 0, 1),
      clamp(Number.isFinite(pieces[3]) ? pieces[3] : 1, 0, 1)
    ];
  }

  function parsePosition(value) {
    const normalized = String(value || "center")
      .trim()
      .toLowerCase();

    if (normalized === "top") return [0.5, 0];
    if (normalized === "bottom") return [0.5, 1];
    if (normalized === "left") return [0, 0.5];
    if (normalized === "right") return [1, 0.5];
    if (normalized === "center") return [0.5, 0.5];

    const parts = normalized.split(/\s+/);
    const parsePart = function (part, fallback) {
      if (!part) return fallback;
      if (part === "left" || part === "top") return 0;
      if (part === "center") return 0.5;
      if (part === "right" || part === "bottom") return 1;
      if (part.endsWith("%")) return clamp(parseFloat(part) / 100, 0, 1);
      return fallback;
    };

    return [parsePart(parts[0], 0.5), parsePart(parts[1], 0.5)];
  }

  function getCornerRadii(snapshot, width, height) {
    const maximum = Math.min(width, height) * 0.5;
    const parsed = String(snapshot.radiusText || snapshot.radius || maximum)
      .match(/-?(?:\d+\.?\d*|\.\d+)/g);
    const values = (parsed || [])
      .map(Number)
      .filter(Number.isFinite)
      .map((value) => clamp(value, 0, maximum));
    let topLeft = maximum;
    let topRight = maximum;
    let bottomRight = maximum;
    let bottomLeft = maximum;

    if (values.length === 1) {
      topLeft = topRight = bottomRight = bottomLeft = values[0];
    } else if (values.length === 2) {
      topLeft = bottomRight = values[0];
      topRight = bottomLeft = values[1];
    } else if (values.length === 3) {
      topLeft = values[0];
      topRight = bottomLeft = values[1];
      bottomRight = values[2];
    } else if (values.length >= 4) {
      topLeft = values[0];
      topRight = values[1];
      bottomRight = values[2];
      bottomLeft = values[3];
    }

    return [bottomRight, topRight, bottomLeft, topLeft];
  }

  function create(options) {
    const settings = options || {};
    const root = settings.root;
    const surface = settings.surface;
    const canvas = settings.canvas;
    const image = settings.image;
    const stage = settings.stage;
    const overlay = settings.overlay || null;

    if (!root || !surface || !canvas || !image || !stage) {
      return {
        render: function () {},
        setVisible: function () {},
        refresh: function () {},
        destroy: function () {}
      };
    }

    let gl = null;
    let program = null;
    let buffer = null;
    let texture = null;
    let locations = null;
    let requestedUrl = "";
    let failedUrl = "";
    let textureWidth = 0;
    let textureHeight = 0;
    let loadToken = 0;
    let ready = false;
    let visible = false;
    let destroyed = false;
    let contextLost = false;
    let lastSnapshot = null;
    let resizeObserver = null;
    let drawCount = 0;

    function setState(state) {
      if (destroyed) return;
      if (state) root.setAttribute("data-lt-refraction-state", state);
      else root.removeAttribute("data-lt-refraction-state");
    }

    function clearGlResources() {
      if (gl && !contextLost) {
        if (texture) gl.deleteTexture(texture);
        if (buffer) gl.deleteBuffer(buffer);
        if (program) gl.deleteProgram(program);
      }
      texture = null;
      buffer = null;
      program = null;
      locations = null;
      ready = false;
    }

    function fail(rememberUrl) {
      if (destroyed) return;
      ready = false;
      if (rememberUrl && requestedUrl) failedUrl = requestedUrl;
      setState("fallback");
    }

    function initializeGl() {
      if (gl && program && !contextLost) return true;
      if (useAccessibleFallback()) {
        return false;
      }

      try {
        gl = canvas.getContext("webgl", {
          alpha: true,
          antialias: false,
          depth: false,
          stencil: false,
          premultipliedAlpha: false,
          preserveDrawingBuffer: false,
          powerPreference: "low-power"
        });
      } catch (error) {
        gl = null;
      }

      if (!gl) return false;

      program = buildProgram(gl);
      if (!program) return false;

      buffer = gl.createBuffer();
      if (!buffer) return false;
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
        gl.STATIC_DRAW
      );

      locations = {
        position: gl.getAttribLocation(program, "a_position"),
        texture: gl.getUniformLocation(program, "u_texture"),
        lensSize: gl.getUniformLocation(program, "u_lens_size"),
        center: gl.getUniformLocation(program, "u_center"),
        stageSize: gl.getUniformLocation(program, "u_stage_size"),
        imageOffset: gl.getUniformLocation(program, "u_image_offset"),
        imageSize: gl.getUniformLocation(program, "u_image_size"),
        transform: gl.getUniformLocation(program, "u_transform"),
        normalTransform: gl.getUniformLocation(program, "u_normal_transform"),
        lag: gl.getUniformLocation(program, "u_lag"),
        radii: gl.getUniformLocation(program, "u_radii"),
        edgeWidth: gl.getUniformLocation(program, "u_edge_width"),
        refraction: gl.getUniformLocation(program, "u_refraction"),
        dispersion: gl.getUniformLocation(program, "u_dispersion"),
        opticalStrength: gl.getUniformLocation(program, "u_optical_strength"),
        overlay: gl.getUniformLocation(program, "u_overlay"),
        adjust: gl.getUniformLocation(program, "u_adjust")
      };

      return locations.position >= 0;
    }

    function uploadTexture(sourceImage) {
      if (!initializeGl()) return false;

      const maximumTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 4096;
      let source = sourceImage;
      let temporaryCanvas = null;

      if (
        sourceImage.naturalWidth > maximumTextureSize ||
        sourceImage.naturalHeight > maximumTextureSize
      ) {
        const ratio = Math.min(
          maximumTextureSize / sourceImage.naturalWidth,
          maximumTextureSize / sourceImage.naturalHeight
        );
        temporaryCanvas = document.createElement("canvas");
        temporaryCanvas.width = Math.max(1, Math.floor(sourceImage.naturalWidth * ratio));
        temporaryCanvas.height = Math.max(1, Math.floor(sourceImage.naturalHeight * ratio));
        const context = temporaryCanvas.getContext("2d");
        if (!context) return false;
        context.drawImage(sourceImage, 0, 0, temporaryCanvas.width, temporaryCanvas.height);
        source = temporaryCanvas;
      }

      if (texture) gl.deleteTexture(texture);
      texture = gl.createTexture();
      if (!texture) return false;

      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

      clearGlErrors(gl);
      try {
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          gl.RGBA,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          source
        );
      } catch (error) {
        gl.deleteTexture(texture);
        texture = null;
        return false;
      }

      if (gl.getError() !== gl.NO_ERROR) {
        gl.deleteTexture(texture);
        texture = null;
        return false;
      }

      textureWidth = sourceImage.naturalWidth;
      textureHeight = sourceImage.naturalHeight;
      return true;
    }

    function prepareTexture() {
      if (destroyed || contextLost) return;
      if (useAccessibleFallback()) {
        fail(false);
        return;
      }

      const url = image.currentSrc || image.src || "";
      if (!url) {
        fail(false);
        return;
      }
      if (failedUrl === url) {
        setState("fallback");
        return;
      }
      if (ready && requestedUrl === url) return;
      if (!ready && requestedUrl === url && root.getAttribute("data-lt-refraction-state") === "loading") {
        return;
      }

      requestedUrl = url;
      ready = false;
      const token = ++loadToken;
      setState("loading");

      const loader = new Image();
      loader.crossOrigin = "anonymous";
      loader.decoding = "async";
      loader.onload = function () {
        if (destroyed || token !== loadToken) return;
        if (!uploadTexture(loader)) {
          fail(true);
          return;
        }
        ready = true;
        setState("ready");
        if (visible && lastSnapshot) draw(lastSnapshot);
      };
      loader.onerror = function () {
        if (destroyed || token !== loadToken) return;
        fail(true);
      };
      loader.src = url;
    }

    function getImageGeometry() {
      const stageWidth = stage.clientWidth || 1;
      const stageHeight = stage.clientHeight || 1;
      const imageStyles = getComputedStyle(image);
      const fit = imageStyles.objectFit || "cover";
      const position = parsePosition(imageStyles.objectPosition);
      let renderedWidth = stageWidth;
      let renderedHeight = stageHeight;

      if (fit === "cover" || fit === "contain") {
        const scale =
          fit === "cover"
            ? Math.max(stageWidth / textureWidth, stageHeight / textureHeight)
            : Math.min(stageWidth / textureWidth, stageHeight / textureHeight);
        renderedWidth = textureWidth * scale;
        renderedHeight = textureHeight * scale;
      }

      return {
        stageWidth: stageWidth,
        stageHeight: stageHeight,
        renderedWidth: renderedWidth,
        renderedHeight: renderedHeight,
        offsetX: (stageWidth - renderedWidth) * position[0],
        offsetY: (stageHeight - renderedHeight) * position[1]
      };
    }

    function getOverlay() {
      if (!overlay) return [0, 0, 0, 0];
      const styles = getComputedStyle(overlay);
      const color = parseColor(styles.backgroundColor);
      color[3] *= clamp(parseFloat(styles.opacity) || 0, 0, 1);
      return color;
    }

    function resizeCanvas(width, height) {
      const ratio = Math.min(global.devicePixelRatio || 1, 2);
      const pixelWidth = Math.max(1, Math.round(width * ratio));
      const pixelHeight = Math.max(1, Math.round(height * ratio));
      if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
      }
      gl.viewport(0, 0, pixelWidth, pixelHeight);
    }

    function draw(snapshot) {
      if (
        destroyed ||
        contextLost ||
        document.hidden ||
        !visible ||
        !ready ||
        !texture ||
        !program ||
        useAccessibleFallback()
      ) {
        return;
      }

      const width = root.offsetWidth || snapshot.width || 88;
      const height = root.offsetHeight || snapshot.height || 44;
      const geometry = getImageGeometry();
      const overlayColor = getOverlay();
      const angle = snapshot.angle || 0;
      const cosine = Math.cos(angle);
      const sine = Math.sin(angle);
      const scaleX = snapshot.scaleX || 1;
      const scaleY = snapshot.scaleY || 1;
      const matrixA = cosine * cosine * scaleX + sine * sine * scaleY;
      const matrixB = cosine * sine * (scaleX - scaleY);
      const matrixC = matrixB;
      const matrixD = sine * sine * scaleX + cosine * cosine * scaleY;
      const determinant = Math.max(
        matrixA * matrixD - matrixB * matrixC,
        0.0001
      );
      const cornerRadii = getCornerRadii(snapshot, width, height);

      resizeCanvas(width, height);
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.enableVertexAttribArray(locations.position);
      gl.vertexAttribPointer(locations.position, 2, gl.FLOAT, false, 0, 0);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(locations.texture, 0);
      gl.uniform2f(locations.lensSize, width, height);
      gl.uniform2f(locations.center, snapshot.centerX || 0, snapshot.centerY || 0);
      gl.uniform2f(locations.stageSize, geometry.stageWidth, geometry.stageHeight);
      gl.uniform2f(locations.imageOffset, geometry.offsetX, geometry.offsetY);
      gl.uniform2f(locations.imageSize, geometry.renderedWidth, geometry.renderedHeight);
      gl.uniformMatrix2fv(
        locations.transform,
        false,
        new Float32Array([matrixA, matrixB, matrixC, matrixD])
      );
      gl.uniformMatrix2fv(
        locations.normalTransform,
        false,
        new Float32Array([
          matrixD / determinant,
          -matrixC / determinant,
          -matrixB / determinant,
          matrixA / determinant
        ])
      );
      gl.uniform2f(locations.lag, snapshot.lagX || 0, snapshot.lagY || 0);
      gl.uniform4fv(locations.radii, new Float32Array(cornerRadii));
      gl.uniform1f(locations.edgeWidth, clamp(height * 0.14, 4.5, 7));
      gl.uniform1f(
        locations.refraction,
        10 + clamp(snapshot.opticalStrength || 0, 0, 1) * 4
      );
      gl.uniform1f(locations.dispersion, 0.55);
      gl.uniform1f(
        locations.opticalStrength,
        clamp(snapshot.opticalStrength || 0, 0, 1)
      );
      gl.uniform4fv(locations.overlay, new Float32Array(overlayColor));
      gl.uniform3f(
        locations.adjust,
        (snapshot.brightness || 103) / 100,
        (snapshot.saturation || 110) / 100,
        (snapshot.contrast || 107) / 100
      );
      clearGlErrors(gl);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.disable(gl.BLEND);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      if (gl.getError() !== gl.NO_ERROR) {
        fail(true);
        return;
      }
      drawCount += 1;
    }

    function render(snapshot) {
      if (destroyed) return;
      lastSnapshot = snapshot;
      if (!ready) {
        prepareTexture();
        return;
      }

      const url = image.currentSrc || image.src || "";
      if (url && url !== requestedUrl) {
        prepareTexture();
        return;
      }

      draw(snapshot);
    }

    function setVisible(nextVisible) {
      visible = Boolean(nextVisible);
      if (visible) {
        prepareTexture();
        if (lastSnapshot) draw(lastSnapshot);
      }
    }

    function refresh() {
      requestedUrl = "";
      failedUrl = "";
      ready = false;
      prepareTexture();
    }

    function handleImageLoad() {
      refresh();
    }

    function handleContextLost(event) {
      event.preventDefault();
      contextLost = true;
      clearGlResources();
      fail(false);
    }

    function handleContextRestored() {
      if (destroyed) return;
      contextLost = false;
      gl = null;
      requestedUrl = "";
      failedUrl = "";
      prepareTexture();
    }

    function handleAccessibilityChange() {
      if (useAccessibleFallback()) {
        loadToken += 1;
        clearGlResources();
        setState("fallback");
        return;
      }
      refresh();
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      visible = false;
      loadToken += 1;
      image.removeEventListener("load", handleImageLoad);
      canvas.removeEventListener("webglcontextlost", handleContextLost);
      canvas.removeEventListener("webglcontextrestored", handleContextRestored);
      if (typeof reducedTransparencyQuery.removeEventListener === "function") {
        reducedTransparencyQuery.removeEventListener("change", handleAccessibilityChange);
      }
      if (typeof reducedMotionQuery.removeEventListener === "function") {
        reducedMotionQuery.removeEventListener("change", handleAccessibilityChange);
      }
      if (typeof forcedColorsQuery.removeEventListener === "function") {
        forcedColorsQuery.removeEventListener("change", handleAccessibilityChange);
      }
      if (resizeObserver) resizeObserver.disconnect();
      clearGlResources();
      root.removeAttribute("data-lt-refraction-state");
      canvas.width = 1;
      canvas.height = 1;
      gl = null;
      lastSnapshot = null;
    }

    image.addEventListener("load", handleImageLoad);
    canvas.addEventListener("webglcontextlost", handleContextLost);
    canvas.addEventListener("webglcontextrestored", handleContextRestored);
    if (typeof reducedTransparencyQuery.addEventListener === "function") {
      reducedTransparencyQuery.addEventListener("change", handleAccessibilityChange);
    }
    if (typeof reducedMotionQuery.addEventListener === "function") {
      reducedMotionQuery.addEventListener("change", handleAccessibilityChange);
    }
    if (typeof forcedColorsQuery.addEventListener === "function") {
      forcedColorsQuery.addEventListener("change", handleAccessibilityChange);
    }
    if (typeof ResizeObserver === "function") {
      resizeObserver = new ResizeObserver(function () {
        if (visible && lastSnapshot) draw(lastSnapshot);
      });
      resizeObserver.observe(stage);
      resizeObserver.observe(root);
    }

    return {
      render: render,
      setVisible: setVisible,
      refresh: refresh,
      destroy: destroy,
      getDrawCount: function () {
        return drawCount;
      }
    };
  }

  global.LTLiquidGlassRefraction = Object.freeze({
    version: VERSION,
    create: create
  });
})(window);